import React, { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { math } from 'polished'

// Components
import { EditableRow } from './EditableRow'
import { HeaderCell } from './HeaderCell'
import { StaticCell, S_StaticCell, staticCell } from './StaticCell'
import { S_FormInput } from '../../_components/Forms/FormInput/FormInput.styles'

// Constants
import { tableStylesConfig, tableStyles, tableHeaderStyles, cssBreakpoints } from '../../_constants/styles'

// Redux hooks
import { useDrag } from '../../_reduxHooks/drag.hook'

// Styled Components
export const S_EditableTable = styled.div`
  ${tableStyles}

  min-width: ${(props) => props.minWidth || 0};
  ${S_FormInput}:disabled {
    color: black;
    background-color: white;
    cursor: text;
  }
`

export const S_EditableTableContainer = styled.div`
  width: 100%;
  position: relative;

  // It trims contents of the table by x-axis
  // Make sure that all inner elements works properly
  // f.e. autocomplete dropdown or other absolutely positioned elements
  ${(props) =>
    props.scrollOnMobile &&
    css`
      @media screen and (max-width: ${cssBreakpoints.mobileMd}) {
        overflow-x: auto;
      }
    `}
`

const S_HeaderRow = styled.div`
  ${tableHeaderStyles}
  ${S_StaticCell} {
    border-right: 0;
  }
`

const S_UploadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-style: dotted;
  border-color: #4caf50;
  border-width: 2px;
  width: 100%;
  height: 50px;
  padding: 8px;
`

export const EditableTable = ({
  entities,
  formFields,
  showRowNumbers = true,
  RowControls = null,
  onChange = () => {},
  onBlur = () => {},
  onFilesAccepted = async () => {},
  onErrorsUpdate = () => {},
  errors = {},
  readOnly = false,
  minWidth = false,
  scrollOnMobile = false,
  customStartIndex = false,
}) => {
  // Calculate table min-width
  const numFields = formFields.length + (RowControls !== null ? 1 : 0)
  const calculatedMinWidth = math(`${numFields} * ${tableStylesConfig.cellMinWidth} + ${staticCell.rowNumberWidth}`)
  minWidth = minWidth ? `${minWidth}px` : calculatedMinWidth
  const [fileHoverIndex, setFileHoverIndex] = useState(null)
  const [fileUploadingIndex, setFileUploadingIndex] = useState(null)
  const { drag = {} } = useDrag()
  const dragOverGlobalTarget = drag.dragOverTarget || ''

  useEffect(() => {
    if (dragOverGlobalTarget.constructor === Object && !Object.keys(dragOverGlobalTarget).length) return

    if (
      !dragOverGlobalTarget.includes(S_UploadingRow.styledComponentId) &&
      !dragOverGlobalTarget.includes(S_FormInput.styledComponentId) &&
      !dragOverGlobalTarget.includes(S_EditableTable.styledComponentId)
    ) {
      setFileHoverIndex(null)
    }
  }, [dragOverGlobalTarget])

  return (
    <S_EditableTableContainer minWidth={minWidth} scrollOnMobile={scrollOnMobile}>
      <S_EditableTable
        minWidth={minWidth}
        onDragLeave={(event) => {
          event.preventDefault()
          // Clearing because onDragEnter, onDragEnter can be skiped if mouse passes too fast
          setFileHoverIndex(null)
        }}
        onDropCapture={(e) => {
          e.preventDefault()
          e.stopPropagation()

          const lastHoverIndex = fileHoverIndex

          setFileUploadingIndex(fileHoverIndex)
          setFileHoverIndex(null)

          //when an already uploaded image is being dragged over we'll handle it here instead of the onDrop handler
          let base64Image = e.dataTransfer.getData('base64')
          let imageName = e.dataTransfer.getData('imageName')
          if (base64Image) {
            let imageData = base64Image.split(',')
            let mimeType = imageData[0].replace('data:', '').replace(';base64', '')
            let image = imageData[1]

            //base64 to proper File type, goes byteString -> byteArray (ints) -> byteArray -> blob
            const byteCharacters = atob(image)
            const byteNumbers = new Array(byteCharacters.length)
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i)
            }
            const byteArray = new Uint8Array(byteNumbers)
            const blob = new Blob([byteArray], { type: mimeType })

            onFilesAccepted(
              [
                new File([blob], imageName, {
                  type: mimeType,
                }),
              ],
              entities[lastHoverIndex].id,
              entities[lastHoverIndex]
            ).then(() => {
              setFileHoverIndex(null)
              setFileUploadingIndex(null)
            })

            e.stopPropagation()
          } else {
            setFileHoverIndex(null)
            setFileUploadingIndex(null)
          }
        }}
      >
        {/* Table Header */}
        <S_HeaderRow>
          {/* Row Number Header */}
          {showRowNumbers && <StaticCell className="row-number" />}

          {/* Col Names */}
          {formFields.map((formField, i) => (
            <HeaderCell {...formField} key={i} />
          ))}

          {/* Row Controls */}
          {RowControls && !readOnly && <StaticCell className="row-controls" />}
        </S_HeaderRow>

        {/* Editable Rows */}
        {entities &&
          entities.map((entity, rowIndex) => {
            const rowNumber = rowIndex + 1
            const rowNumberToShow = customStartIndex ? customStartIndex + rowNumber : rowNumber
            const errorsSource = entity?.errors ? entity?.errors : errors
            return fileHoverIndex === rowIndex ? (
              <S_UploadingRow key={entity.id}>Upload Files</S_UploadingRow>
            ) : fileUploadingIndex === rowIndex ? (
              <S_UploadingRow key={entity.id}>Uploading Files...</S_UploadingRow>
            ) : (
              <EditableRow
                RowControls={RowControls}
                entity={entity}
                errors={errorsSource}
                fields={formFields}
                key={entity.id}
                onBlur={onBlur}
                onChange={onChange}
                onDragOver={(event) => {
                  event.preventDefault()
                  const { target } = event

                  if (fileHoverIndex !== rowIndex) {
                    // Clearing because onDragEnter, onDragEnter can be skiped if mouse passes too fast
                    setFileHoverIndex(rowIndex)
                  }
                }}
                onErrorsUpdate={onErrorsUpdate}
                readOnly={readOnly}
                rowNumber={rowNumberToShow}
              />
            )
          })}
      </S_EditableTable>
    </S_EditableTableContainer>
  )
}
